import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { generateAddress } from "../../utils/Utilitats";
import AjudaEntitats from "./AjudaEntitats";
import { BotoAjuda } from "./AjudaModal";

const PAGE_SIZES = [10, 25, 50];
const TIPUS_PREFERITS = ["Client", "Proveidor", "Empleat", "Empresa", "Contacte", "Lliure", "Entitat"];

function clau(r) {
  return r ? `${r.tipus}-${r.id}` : "";
}

function digits(s) {
  return String(s || "").replace(/\D/g, "");
}

function xipsDe(r) {
  if (!r) return [];
  if (r.tipus === "contacte") {
    return r.esLliure ? [{ label: "Lliure", ok: true }] : [{ label: "Contacte" }];
  }
  const parts = String(r.origen || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts.map((label) => ({ label })) : [{ label: "Entitat" }];
}

function buildTipusCatalog(items) {
  const present = [];
  const seen = new Set();
  for (const it of items || []) {
    for (const x of xipsDe(it)) {
      const label = (x.label || "").trim();
      if (!label) continue;
      const k = label.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      present.push(label);
    }
  }
  const ordered = [];
  for (const pref of TIPUS_PREFERITS) {
    const found = present.find((p) => p.toLowerCase() === pref.toLowerCase());
    if (found) ordered.push(found);
  }
  const extras = present
    .filter((p) => !TIPUS_PREFERITS.some((t) => t.toLowerCase() === p.toLowerCase()))
    .sort((a, b) => a.localeCompare(b, "ca"));
  return [...ordered, ...extras];
}

function alignCatalog(catalog, first) {
  if (!catalog.length) return catalog;
  if (!first) return catalog;
  const idx = catalog.findIndex((t) => t.toLowerCase() === String(first).toLowerCase());
  if (idx <= 0) return catalog;
  return [...catalog.slice(idx), ...catalog.slice(0, idx)];
}

function prioritatTipus(row, order) {
  if (!order?.length) return 99;
  let best = order.length;
  for (const x of xipsDe(row)) {
    const i = order.findIndex((t) => t.toLowerCase() === String(x.label).toLowerCase());
    if (i >= 0 && i < best) best = i;
  }
  return best;
}

function Contactesagenda() {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const idEmpresa = empresaSeleccionada?.IdEmpresa ?? null;
  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");

  const [items, setItems] = useState([]);
  const [relacionsCE, setRelacionsCE] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [vista, setVista] = useState("llista");
  const [filterText, setFilterText] = useState("");
  const [filterTelefon, setFilterTelefon] = useState("");
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "origen", direction: "asc", tipusOrder: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isNarrow, setIsNarrow] = useState(false);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [vincCerca, setVincCerca] = useState("");
  const [vincEntitatId, setVincEntitatId] = useState("");
  const [vincBusy, setVincBusy] = useState(false);
  const [showLliureModal, setShowLliureModal] = useState(false);
  const [menuAccions, setMenuAccions] = useState(false);
  const menuRef = useRef(null);

  const handleEsc = useCallback(() => {
    if (showLliureModal) {
      setShowLliureModal(false);
      return;
    }
    if (vista !== "llista") {
      setVista("llista");
      setMenuAccions(false);
    }
  }, [showLliureModal, vista]);
  TeclaEscEnter(handleEsc, null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const entitatsById = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (it.tipus === "entitat") map.set(it.id, it);
    }
    return map;
  }, [items]);

  const getEntitatsIds = useCallback(
    (idContacte) => {
      const ids = relacionsCE?.[String(idContacte)];
      return Array.isArray(ids) ? ids.map(Number).filter(Boolean) : [];
    },
    [relacionsCE]
  );

  const fetchAll = useCallback(async () => {
    if (!idEmpresa) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/Contactesagenda.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa }),
      });
      const data = await res.json();
      const arrEnts = Array.isArray(data?.entitats) ? data.entitats : [];
      const arrConts = Array.isArray(data?.contactes) ? data.contactes : [];
      const relMap =
        data?.relacionsContactesEntitats && typeof data.relacionsContactesEntitats === "object"
          ? data.relacionsContactesEntitats
          : {};
      const numMap =
        data?.numEntitatsPerContacte && typeof data.numEntitatsPerContacte === "object"
          ? data.numEntitatsPerContacte
          : {};

      setRelacionsCE(relMap);

      const list = [...arrEnts, ...arrConts].map((r) => {
        const tipus = (r.tipus || (r.idContactes ? "contacte" : "entitat")).toLowerCase();
        const id =
          r.idContactes != null
            ? Number(r.idContactes)
            : r.idEntitats != null
            ? Number(r.idEntitats)
            : r.id != null
            ? Number(r.id)
            : null;
        const nom = r.NomMostrar ?? [r.Nom, r.Cognom1, r.Cognom2].filter(Boolean).join(" ").trim();
        const telefon = (r.Telefon || r.TelefonMobil || r.TelefonFixe || "").toString();
        const email = (r.Email || r.Email1 || r.Email2 || "").toString();
        const adreca = {
          TipusVia: r.TipusVia ?? r.tipusVia ?? "",
          NomVia: r.NomVia ?? r.nomVia ?? "",
          NumVia: r.NumVia ?? r.Numero ?? r.numVia ?? "",
          EscalaVia: r.EscalaVia ?? r.Escala ?? "",
          Pis: r.Pis ?? "",
          Porta: r.Porta ?? "",
        };
        const entitatsIds =
          tipus === "contacte" && id != null
            ? Array.isArray(relMap?.[String(id)])
              ? relMap[String(id)].map(Number).filter(Boolean)
              : []
            : [];
        const numEntitats =
          tipus === "contacte" && id != null
            ? Number.isFinite(Number(numMap?.[String(id)]))
              ? Number(numMap[String(id)])
              : entitatsIds.length
            : 0;
        return {
          tipus,
          id,
          origen: r.Rol || "",
          nom,
          nomComercial: r.NomComercial ?? null,
          telefon,
          email,
          adrecaTxt: generateAddress(adreca),
          poblacio: r.Poblacio ?? r.poblacio ?? "",
          entitatsIds,
          numEntitats,
          esLliure: tipus === "contacte" ? numEntitats === 0 : false,
        };
      });
      setItems(list);
    } catch (err) {
      console.error("Error carregant Contactesagenda:", err);
      setItems([]);
      setRelacionsCE({});
    } finally {
      setLoading(false);
      setSelected(null);
      setVista("llista");
      setMenuObert(null);
      setVincCerca("");
      setVincEntitatId("");
      setShowLliureModal(false);
    }
  }, [idEmpresa]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filtered = useMemo(() => {
    const t = (filterText || "").toLowerCase().trim();
    const tel = digits(filterTelefon);
    return items.filter((it) => {
      if (tel && !digits(it.telefon).includes(tel)) return false;
      if (!t) return true;
      const vinculats =
        it.tipus === "contacte"
          ? (it.entitatsIds || [])
              .map((idE) => entitatsById.get(idE)?.nom)
              .filter(Boolean)
              .join(" ")
          : "";
      const fields = [it.origen, it.nom, it.nomComercial, it.telefon, it.email, it.adrecaTxt, it.poblacio, vinculats]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return fields.includes(t);
    });
  }, [items, filterText, filterTelefon, entitatsById]);

  const sorted = useMemo(() => {
    const { key, direction, tipusOrder } = sortConfig;
    const mult = direction === "asc" ? 1 : -1;
    const copy = [...filtered];
    copy.sort((a, b) => {
      if (key === "origen") {
        const ordre = tipusOrder?.length ? tipusOrder : buildTipusCatalog(items);
        const A = prioritatTipus(a, ordre);
        const B = prioritatTipus(b, ordre);
        if (A !== B) return A - B;
        return (a.nom || "").localeCompare(b.nom || "", "ca");
      }
      const A = (a[key] || "").toString().toLowerCase();
      const B = (b[key] || "").toString().toLowerCase();
      if (A < B) return -1 * mult;
      if (A > B) return 1 * mult;
      return 0;
    });
    return copy;
  }, [filtered, sortConfig, items]);

  useEffect(() => {
    setPage(1);
  }, [filterText, filterTelefon, sortConfig]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = sorted.slice(inici, inici + pageSize);

  const currentIndex = useMemo(() => {
    if (!selected) return -1;
    return sorted.findIndex((r) => clau(r) === clau(selected));
  }, [sorted, selected]);

  useEffect(() => {
    const catalog = buildTipusCatalog(items);
    setSortConfig((prev) => {
      if (prev.key !== "origen") return prev;
      if (!catalog.length) return { ...prev, tipusOrder: [] };
      if (!prev.tipusOrder?.length) return { ...prev, tipusOrder: catalog };
      return { ...prev, tipusOrder: alignCatalog(catalog, prev.tipusOrder[0]) };
    });
  }, [items]);

  const handleSort = (key) => {
    if (key === "origen") {
      const catalog = buildTipusCatalog(items);
      setSortConfig((prev) => {
        if (prev.key !== "origen" || !prev.tipusOrder?.length) {
          return { key: "origen", direction: "asc", tipusOrder: catalog };
        }
        const aligned = alignCatalog(catalog, prev.tipusOrder[0]);
        const rotated = aligned.length > 1 ? [...aligned.slice(1), aligned[0]] : aligned;
        return { key: "origen", direction: "asc", tipusOrder: rotated };
      });
      return;
    }
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction, tipusOrder: sortConfig.tipusOrder });
  };

  const obrirFitxa = (row) => {
    setMenuObert(null);
    setSelected(row);
    setVista("fitxa");
    setVincCerca("");
    setVincEntitatId("");
    setMenuAccions(false);
    setShowLliureModal(false);
  };

  const tornarLlista = () => {
    setVista("llista");
    setMenuAccions(false);
    setShowLliureModal(false);
  };

  const goPrev = () => {
    if (!sorted.length) return;
    if (currentIndex <= 0) setSelected(sorted[sorted.length - 1]);
    else setSelected(sorted[currentIndex - 1]);
    setVincCerca("");
    setVincEntitatId("");
    setMenuAccions(false);
  };

  const goNext = () => {
    if (!sorted.length) return;
    if (currentIndex < 0 || currentIndex >= sorted.length - 1) setSelected(sorted[0]);
    else setSelected(sorted[currentIndex + 1]);
    setVincCerca("");
    setVincEntitatId("");
    setMenuAccions(false);
  };

  const obrirMenu = (e, row) => {
    e.stopPropagation();
    const id = clau(row);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 90;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
    setMenuPos({ top, left });
    setMenuObert(id);
    setSelected(row);
  };

  const aplicarRelacioLocal = (idContacte, ids, nouRol) => {
    const net = ids.map(Number).filter(Boolean);
    const patch = (it) =>
      it.tipus === "contacte" && it.id === Number(idContacte)
        ? {
            ...it,
            entitatsIds: net,
            numEntitats: net.length,
            esLliure: net.length === 0,
            origen: nouRol || (net.length === 0 ? "Altres" : it.origen),
          }
        : it;
    setItems((prev) => prev.map(patch));
    setSelected((prev) => (prev ? patch(prev) : prev));
    setRelacionsCE((prev) => ({ ...prev, [String(idContacte)]: net }));
  };

  const addRelacio = async (idContacte, idEntitat) => {
    if (!idEmpresa || !idContacte || !idEntitat) return;
    setVincBusy(true);
    try {
      const res = await fetch(`${API_URL}/Afegircontacteentitat.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa, idContacte, idEntitat }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || "No s'ha pogut vincular el contacte a l'entitat.");
        return;
      }
      const ids = getEntitatsIds(idContacte);
      if (!ids.includes(Number(idEntitat))) ids.push(Number(idEntitat));
      aplicarRelacioLocal(idContacte, ids, data?.nouRol);
      setVincEntitatId("");
      setVincCerca("");
    } catch (err) {
      console.error("Error addRelacio:", err);
      alert("Error de comunicació amb el servidor.");
    } finally {
      setVincBusy(false);
    }
  };

  const removeRelacio = async (idContacte, idEntitat) => {
    if (!idEmpresa || !idContacte || !idEntitat) return;
    setVincBusy(true);
    try {
      const res = await fetch(`${API_URL}/Eliminarcontacteentitat.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa, idContacte, idEntitat }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || "No s'ha pogut desvincular el contacte de l'entitat.");
        return;
      }
      aplicarRelacioLocal(
        idContacte,
        getEntitatsIds(idContacte).filter((x) => Number(x) !== Number(idEntitat)),
        data?.nouRol
      );
    } catch (err) {
      console.error("Error removeRelacio:", err);
      alert("Error de comunicació amb el servidor.");
    } finally {
      setVincBusy(false);
    }
  };

  const deixarLliure = async () => {
    if (!selected || selected.tipus !== "contacte") return;
    setVincBusy(true);
    try {
      const res = await fetch(`${API_URL}/Eliminarcontacteentitats.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa, idContacte: selected.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || "No s'han pogut eliminar les vinculacions del contacte.");
        return;
      }
      aplicarRelacioLocal(selected.id, [], "Altres");
      setShowLliureModal(false);
    } catch (err) {
      console.error("Error deixarLliure:", err);
      alert("Error de comunicació amb el servidor.");
    } finally {
      setVincBusy(false);
    }
  };

  const contactesDeEntitat = (idEntitat) =>
    items.filter((it) => it.tipus === "contacte" && (it.entitatsIds || []).includes(Number(idEntitat)));

  const nomsVinculats = (row) => {
    if (row.tipus !== "contacte") return "";
    const noms = (row.entitatsIds || []).map((idE) => entitatsById.get(idE)?.nom).filter(Boolean);
    return noms.join(" · ");
  };

  const th = (key, label) => {
    const esTipus = key === "origen";
    const actiu = sortConfig.key === key;
    const extra = esTipus && actiu && sortConfig.tipusOrder?.[0] ? ` · ${sortConfig.tipusOrder[0]}` : "";
    const fletxa = actiu && !esTipus ? (sortConfig.direction === "asc" ? " \u2191" : " \u2193") : "";
    return (
      <th className={actiu ? "is-sorted" : ""} onClick={() => handleSort(key)}>
        {label}
        {extra}
        {fletxa}
      </th>
    );
  };

  const renderXips = (row) =>
    xipsDe(row).map((x) => (
      <span key={x.label} className={`dv2-chip ${x.ok ? "dv2-chip--ok" : ""}`}>
        {x.label}
      </span>
    ));

  const renderAccions = (row) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === clau(row) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, row)}
      >
        …
      </button>
    </div>
  );

  const paginesVisibles = () => {
    const pages = [];
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = paginaActual - 1; i <= paginaActual + 1; i += 1) {
      if (i > 1 && i < totalPages) afegir(i);
    }
    if (totalPages > 1) afegir(totalPages);
    return pages.sort((a, b) => a - b);
  };

  const camp = (label, content, mida = "mig") => {
    const extra =
      mida === "ample"
        ? "dv2-field--ample"
        : mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className="dv2-label">{label}</span>
        {content}
      </label>
    );
  };

  const rowMenu = menuObert ? sorted.find((r) => clau(r) === menuObert) : null;

  const renderLlista = () => (
    <>
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className="dv2-pageHeader-icon" aria-hidden="true">
            📞
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">Contactes</h1>
            <p className="dv2-pageHeader-subtitle">
              Directori de l'empresa: clients, proveïdors, empleats i contactes. Cerca per nom o pel número que truca.
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funciona la cerca de Contactes" onClick={() => setMostraAjuda(true)} />
        </div>
      </div>

      <div className="dv2-listBody">
        <div className="dv2-filterBar">
          <div className="dv2-filter">
            <span className="dv2-filter-label">Qui truca</span>
            <input
              type="text"
              inputMode="tel"
              className="dv2-input"
              placeholder="Número de telèfon"
              value={filterTelefon}
              onChange={(e) => setFilterTelefon(e.target.value)}
            />
          </div>
          <div className="dv2-filter dv2-filter--grow">
            <span className="dv2-filter-label">Cerca</span>
            <input
              type="text"
              className="dv2-input dv2-input-search"
              placeholder="Nom, email, adreça, origen…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <p className="dv2-empty">Carregant…</p>
        ) : sorted.length === 0 ? (
          <p className="dv2-empty">No s'ha trobat ningú amb aquests criteris.</p>
        ) : isNarrow ? (
          <div className="dv2-recordList" style={{ display: "flex" }}>
            {paginaItems.map((row) => (
              <article
                key={clau(row)}
                className={`dv2-record ${selected && clau(selected) === clau(row) ? "is-selected" : ""}`}
                onClick={() => setSelected(selected && clau(selected) === clau(row) ? null : row)}
                onDoubleClick={() => pucConsultar && obrirFitxa(row)}
              >
                <div>
                  <p className="dv2-record-title">{row.nom || "\u2014"}</p>
                  <p className="dv2-record-meta">
                    {[row.telefon, row.email, row.poblacio].filter(Boolean).join(" · ") || "\u2014"}
                  </p>
                  <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>{renderXips(row)}</div>
                </div>
                {renderAccions(row)}
              </article>
            ))}
          </div>
        ) : (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  {th("origen", "Tipus")}
                  {th("nom", "Nom")}
                  {th("telefon", "Telèfon")}
                  {th("email", "Email")}
                  {th("poblacio", "Població")}
                  <th>Vinculat a</th>
                  <th className="dv2-table-actions">Accions</th>
                </tr>
              </thead>
              <tbody>
                {paginaItems.map((row) => (
                  <tr
                    key={clau(row)}
                    className={selected && clau(selected) === clau(row) ? "is-selected" : ""}
                    onClick={() => setSelected(selected && clau(selected) === clau(row) ? null : row)}
                    onDoubleClick={() => pucConsultar && obrirFitxa(row)}
                  >
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{renderXips(row)}</div>
                    </td>
                    <td>{row.nom || "\u2014"}</td>
                    <td>{row.telefon || "\u2014"}</td>
                    <td className="dv2-cell-clip" title={row.email || ""}>
                      {row.email || "\u2014"}
                    </td>
                    <td>{row.poblacio || "\u2014"}</td>
                    <td className="dv2-cell-clip" title={nomsVinculats(row)}>
                      {row.tipus === "contacte" ? nomsVinculats(row) || "Lliure" : "\u2014"}
                    </td>
                    <td className="dv2-table-actions">{renderAccions(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {sorted.length > 0 && (
          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)}>
                {"\u00AB"}
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {"\u2039"}
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {"\u203A"}
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                {"\u00BB"}
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      {rowMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(rowMenu)}>
                Consultar
              </button>
            )}
          </div>,
          document.body
        )}
    </>
  );

  const renderFitxa = () => {
    if (!selected) return null;
    const r = selected;
    const esContacte = r.tipus === "contacte";
    const ids = esContacte ? r.entitatsIds || getEntitatsIds(r.id) : [];
    const entitatsActuals = ids.map((idE) => entitatsById.get(Number(idE))).filter(Boolean);
    const cercaEnt = (vincCerca || "").toLowerCase().trim();
    const entitatsOpcio = items
      .filter((it) => it.tipus === "entitat" && !ids.includes(it.id))
      .filter((it) => !cercaEnt || `${it.nom} ${it.origen}`.toLowerCase().includes(cercaEnt))
      .sort((a, b) => (a.nom || "").localeCompare(b.nom || "", "ca"));
    const contactesEntitat = !esContacte ? contactesDeEntitat(r.id) : [];

    return (
      <div className="dv2-entity">
        {showLliureModal && (
          <div
            className="dv2-modalOverlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (!vincBusy && e.target === e.currentTarget) setShowLliureModal(false);
            }}
          >
            <div className="dv2-modal">
              <h3 className="dv2-modal-title">Deixar el contacte lliure</h3>
              <p className="dv2-modal-lead">
                S'eliminaran totes les vinculacions d'aquest contacte. Les agendes existents conservaran l'entitat original.
              </p>
              <div className="dv2-modal-actions">
                <button type="button" className="dv2-btn" disabled={vincBusy} onClick={() => setShowLliureModal(false)}>
                  Cancel·lar
                </button>
                <button type="button" className="dv2-btn dv2-btn--danger" disabled={vincBusy} onClick={deixarLliure}>
                  Deixar lliure
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="dv2-pageHeader">
          <div className="dv2-pageHeader-main">
            <div className="dv2-pageHeader-icon" aria-hidden="true">
              📞
            </div>
            <div>
              <h1 className="dv2-pageHeader-title">{r.nom || "Contacte"}</h1>
              <p className="dv2-pageHeader-subtitle">{[r.telefon, r.email].filter(Boolean).join(" · ") || "Sense telèfon ni email"}</p>
            </div>
          </div>
          <div className="dv2-pageHeader-actions">
            <BotoAjuda title="Com funciona la cerca de Contactes" onClick={() => setMostraAjuda(true)} />
            <button type="button" className="dv2-btn" onClick={tornarLlista}>
              Tornar
            </button>
            {esContacte && pucModificar && ids.length > 0 && (
              <div style={{ position: "relative" }}>
                <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                  Més accions
                </button>
                {menuAccions && (
                  <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                    <button
                      type="button"
                      className="dv2-menu-item"
                      onClick={() => {
                        setMenuAccions(false);
                        setShowLliureModal(true);
                      }}
                    >
                      Deixar lliure
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="dv2-entity-body">
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">1</span>
              <h2 className="dv2-card-title">Dades de contacte</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {camp("Tipus", <span className="dv2-value">{renderXips(r)}</span>, "mig")}
              {camp("Origen", <span className="dv2-value">{r.origen || "\u2014"}</span>, "mig")}
              {camp("Nom", <span className="dv2-value">{r.nom || "\u2014"}</span>, "llarg")}
              {r.nomComercial
                ? camp("Nom comercial", <span className="dv2-value">{r.nomComercial}</span>, "llarg")
                : null}
              {camp("Telèfon", <span className="dv2-value">{r.telefon || "\u2014"}</span>, "mig")}
              {camp("Email", <span className="dv2-value">{r.email || "\u2014"}</span>, "llarg")}
              {camp("Adreça", <span className="dv2-value">{r.adrecaTxt || "\u2014"}</span>, "ample")}
              {camp("Població", <span className="dv2-value">{r.poblacio || "\u2014"}</span>, "mig")}
            </div>
          </section>

          {esContacte ? (
            <section className="dv2-card">
              <div className="dv2-card-head">
                <span className="dv2-card-num">2</span>
                <h2 className="dv2-card-title">Entitats vinculades</h2>
              </div>
              {entitatsActuals.length === 0 ? (
                <p className="dv2-empty">Aquest contacte és lliure: no està vinculat a cap entitat.</p>
              ) : (
                <div className="dv2-specList">
                  {entitatsActuals.map((e) => (
                    <div key={`ce-${r.id}-${e.id}`} className="dv2-spec">
                      <span className="dv2-spec-name">{e.nom || `Entitat #${e.id}`}</span>
                      <span className="dv2-spec-value">
                        {e.origen || "\u2014"}
                        {pucModificar && (
                          <button
                            type="button"
                            className="dv2-btn dv2-btn--compact"
                            style={{ marginLeft: 10 }}
                            disabled={vincBusy}
                            onClick={() => removeRelacio(r.id, e.id)}
                            title="Desvincular"
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {pucModificar && (
                <div className="dv2-fields dv2-fields--sizes" style={{ marginTop: 14 }}>
                  {camp(
                    "Cerca entitat",
                    <input
                      type="text"
                      className="dv2-input"
                      value={vincCerca}
                      onChange={(e) => setVincCerca(e.target.value)}
                      placeholder="Nom o tipus…"
                      disabled={vincBusy}
                    />,
                    "mig"
                  )}
                  {camp(
                    "Afegir vinculació",
                    <div className="dv2-controlRow">
                      <select
                        className="dv2-select"
                        value={vincEntitatId}
                        onChange={(e) => setVincEntitatId(e.target.value)}
                        disabled={vincBusy}
                      >
                        <option value="">Selecciona una entitat</option>
                        {entitatsOpcio.map((e) => (
                          <option key={`ent-${e.id}`} value={String(e.id)}>
                            {e.nom || `Entitat #${e.id}`}
                            {e.origen ? ` · ${e.origen}` : ""}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="dv2-btn dv2-btn--compact dv2-btn--primary"
                        disabled={vincBusy || !vincEntitatId}
                        onClick={() => addRelacio(r.id, Number(vincEntitatId))}
                      >
                        Vincular
                      </button>
                    </div>,
                    "llarg"
                  )}
                </div>
              )}
            </section>
          ) : (
            <section className="dv2-card">
              <div className="dv2-card-head">
                <span className="dv2-card-num">2</span>
                <h2 className="dv2-card-title">Contactes d'aquesta entitat</h2>
              </div>
              {contactesEntitat.length === 0 ? (
                <p className="dv2-empty">No hi ha contactes vinculats a aquesta entitat.</p>
              ) : (
                <div className="dv2-specList">
                  {contactesEntitat.map((c) => (
                    <button
                      type="button"
                      key={`ec-${c.id}`}
                      className="dv2-spec"
                      style={{ width: "100%", textAlign: "left", cursor: "pointer", border: 0, background: "transparent" }}
                      onClick={() => setSelected(c)}
                    >
                      <span className="dv2-spec-name">{c.nom || "\u2014"}</span>
                      <span className="dv2-spec-value">{[c.telefon, c.email].filter(Boolean).join(" · ") || "\u2014"}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        <div className="dv2-entity-dock">
          {sorted.length > 1 && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={goPrev} title="Anterior">
                {"\u2039"}
              </button>
              <span className="dv2-recordNav-info">
                {currentIndex + 1} / {sorted.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={goNext} title="Següent">
                {"\u203A"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="dv2 dv2-page dv2-page--fill">
      {mostraAjuda && <AjudaEntitats pestanya="contactesCerca" onTancar={() => setMostraAjuda(false)} />}
      {vista === "llista" ? renderLlista() : renderFitxa()}
    </div>
  );
}

export default Contactesagenda;
